import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ErkomByID,
  ErkomFormValue,
  ErkomListQueryParam,
  FetchErkomByIDResponse,
  FetchErkomListQueryParam,
  FetchErkomListResponse,
  FetchImplantTypesQueryParams,
  FetchImplantTypesResponse,
  LocationByProcedure
} from '@shared/models';
import { buildHttpParamsFromQueryParams } from '@shared/utils';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ComercialStatesDocumentsPayload } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class ErkomService {
  _http = inject(HttpClient);
  _apiUrl = environment.apiUrl;
  _authHeaders = new HttpHeaders({
    Authorization: '',
  });

  private currentOrder: ErkomByID | null = null;
  private currentLocationByProcedure: LocationByProcedure = {name:'', id: 0};
  private allLocationsFromProcedure: LocationByProcedure[] | null = null;
  private implant_quantity: undefined

  setCurrentOrder(order: ErkomByID): void {
    sessionStorage.setItem('currentOrder', JSON.stringify(order));
    this.currentOrder = order;
  }

  getCurrentOrder(): ErkomByID | null {
    const order = sessionStorage.getItem('currentOrder');
    if (order) {
      return JSON.parse(order);
    }
    return this.currentOrder;
  }

  clearCurrentOrder(): void {
    this.currentOrder = null;
  }

  setAllLocationsFromProcedure(locations: LocationByProcedure[]) {
    sessionStorage.setItem('allLocationsFromProcedure', JSON.stringify(locations));
    this.allLocationsFromProcedure = locations;
  }

  getAllLocationsFromProcedure(): LocationByProcedure[] | null {
    const locations = sessionStorage.getItem('allLocationsFromProcedure');
    if (locations) {
      return JSON.parse(locations);
    }
    return this.allLocationsFromProcedure;
  }

  setCurrentLocationByProcedure(location: LocationByProcedure) {
    sessionStorage.setItem('currentLocationByProcedure', JSON.stringify(location));
    sessionStorage.setItem('selected_implant_id', JSON.stringify(location.id));
    this.currentLocationByProcedure = location;
  }

  getCurrentLocationByProcedure() {
    const location = sessionStorage.getItem('currentLocationByProcedure');
    if (location) {
      return JSON.parse(location);
    }
    return this.currentLocationByProcedure;
  }

  clearCurrentLocationByProcedure(): void {
    this.currentLocationByProcedure = {name: '', id: 0,};

  }

  private buildPayload(value: ErkomFormValue, isUpdate: boolean = false) {
    const payload: any = {
      implant_test: value.implantTestDate,
      surgery: value.surgeryDate,
      surgery_test: value.fakeSurgeryCompleted,
      surgery_location_id: value.surgeryLocation
        ? Number(value.surgeryLocation)
        : null,
      procedure_id: value.procedure_id || null,
      number_implants: value.number_implants || null,
      cryoanalgesia: value.cryoanalgesia,
      observation: value.observations || null,
      material: value.implantMaterial,
      zip_path: value.zip_path || null,
      // `create` usa `tutorName` y `edit` usa `tutor_name`.
      tutor_name: value.tutor_name ?? value.tutorName ?? null,
    };

    if (!isUpdate) {
      payload.patient_id = value.patientId;
    }

    return payload;
  }

  createErkom(value: ErkomFormValue) {
    const payload = this.buildPayload(value);

    return this._http.post(`${this._apiUrl}/api/order/erkom`, payload, {
      headers: this._authHeaders,
    });
  }

  updateErkom(value: ErkomFormValue, id: number) {
    const payload = this.buildPayload(value);
    return this._http.patch(
      `${this._apiUrl}/api/order/erkom/${id}`,
      payload,
      {
        headers: this._authHeaders,
      }
    );
  }

  getZipPresignedUrl(): Observable<{status: boolean, data: {url: string, path: string}}> {
    return this._http.get<{status: boolean, data: {url: string, path: string}}>(`${this._apiUrl}/api/order/erkom/presigned-url`, {
      headers: this._authHeaders
    });
  }


  fetchErkomOrders(
    queryParams?: Partial<ErkomListQueryParam>
  ): Observable<FetchErkomListResponse> {
    const queryParamsMapped: Partial<FetchErkomListQueryParam> = {
      order_by: queryParams?.order_by,
      order_direction: queryParams?.order_direction,
      page: queryParams?.page,
      per_page: queryParams?.per_page,
      comertial_status: queryParams?.comertial_status,
      clinical_status: queryParams?.clinical_status,
      patient: queryParams?.patient,
      id_number: queryParams?.id_number,
      medic: queryParams?.medic
    };
    const params = buildHttpParamsFromQueryParams(queryParamsMapped);

    return this._http.get<FetchErkomListResponse>(
      `${this._apiUrl}/api/order/erkom/list`,
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  fetchErkomByID(id: number): Observable<ErkomByID> {

    return this._http
      .get<FetchErkomByIDResponse>(`${this._apiUrl}/api/order/erkom/${id}`, {
        headers: this._authHeaders,
      })
      .pipe(map((resp) => resp.data));
  }

  removeErkomByID(id: number) {
    return this._http.delete(`${this._apiUrl}/api/order/delete-erkom/${id}`, {
      headers: this._authHeaders,
    });
  }

  fetchImplantType(
    queryParams?: Partial<FetchImplantTypesQueryParams>
  ): Observable<FetchImplantTypesResponse> {
    let params = new HttpParams();
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }

    return this._http.get<FetchImplantTypesResponse>(
      `${this._apiUrl}/api/implant-type/list`,
      {
        headers: this._authHeaders,
        params,
      }
    );
  }

  updateStatusErkom(nextStatus: number, id: number, observations: string|null = null) {
    const queryParamsMapped = {
      new_status_id: nextStatus,
      implant_test_observations: observations
    };
    const params = buildHttpParamsFromQueryParams(queryParamsMapped);


    return this._http.patch(
      `${this._apiUrl}/api/order/update-status-erkom/${id}`,
      params,
      {
        headers: this._authHeaders,
      }
    );
  }

  updateComercialStatusErkom(id: number, formData: ComercialStatesDocumentsPayload) {
    const form = new FormData(
    );

    form.append('new_status_id', JSON.stringify(formData.new_status_id));
    if (formData.quotation) {
      form.append('quotation', formData.quotation);
    }
    if (formData.bill) {
      form.append('bill', formData.bill);
    }
    if (formData.bill_code) {
      form.append('bill_code', JSON.stringify(formData.bill_code));
    }
    if (formData.manufacturing_code) {
      form.append('manufacturing_code', JSON.stringify(formData.manufacturing_code));
    }
    if (formData.manufacturing_status !== undefined) {
      form.append('manufacturing_status', formData.manufacturing_status ? '1' : '0');
    }
    if (formData.referral) {
      form.append('referral', formData.referral);
    }
    if (formData.referral_code) {
      form.append('referral_code', formData.referral_code);
    }
    form.append('_method', 'PATCH');
    return this._http.post(
      (`${this._apiUrl}/api/order/update-comercial-status/${id}`),
      form,
      {
        headers: this._authHeaders,
      }
    );
  }



}
